import React, { useState, useEffect, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { motion, useInView, AnimatePresence } from "framer-motion";
import indiaGeo from "../data/india.json";
import maharashtraGeo from "../data/maharashtra.json";

const interventionAreas = [
    "Sangli", "Satara", "Kolhapur", "Pune", "Raigad", "Palghar",
    "Solapur", "Ratnagiri", "Sindhudurg", "Osmanabad", "Latur",
    "Yavatmal", "Ahilyanagar", "Goa",
];

const markerCoords = [
    { name: "Sangli", coords: [74.6, 17.0] },
    { name: "Satara", coords: [74.0, 17.7] },
    { name: "Kolhapur", coords: [74.2, 16.7] },
    { name: "Pune", coords: [73.9, 18.5] },
    { name: "Raigad", coords: [73.2, 18.5] },
    { name: "Palghar", coords: [72.8, 19.7] },
    { name: "Solapur", coords: [75.9, 17.7] },
    { name: "Ratnagiri", coords: [73.3, 17.0] },
    { name: "Sindhudurg", coords: [73.7, 16.0] },
    { name: "Osmanabad", coords: [76.0, 18.2] },
    { name: "Latur", coords: [76.6, 18.4] },
    { name: "Yavatmal", coords: [78.1, 20.4] },
    { name: "Ahilyanagar", coords: [74.7, 19.1] },
];

const districtCentroids = [
    { name: "Gondia", coords: [80.13, 21.09] },
    { name: "Bhandara", coords: [79.80, 21.03] },
    { name: "Jalgaon", coords: [75.44, 20.82] },
    { name: "Wardha", coords: [78.59, 20.78] },
    { name: "Buldhana", coords: [76.43, 20.52] },
    { name: "Akola", coords: [77.05, 20.71] },
    { name: "Nashik", coords: [74.07, 20.24] },
    { name: "Gadchiroli", coords: [80.28, 19.90] },
    { name: "Washim", coords: [77.20, 20.25] },
    { name: "Chandrapur", coords: [79.35, 20.12] },
    { name: "Yavatmal", coords: [78.06, 20.00] },
    { name: "Jalna", coords: [76.01, 19.88] },
    { name: "Ahmednagar", coords: [74.75, 19.19] },
    { name: "Hingoli", coords: [77.08, 19.61] },
    { name: "Nanded", coords: [77.69, 19.19] },
    { name: "Parbhani", coords: [76.67, 19.32] },
    { name: "Pune", coords: [74.14, 18.50] },
    { name: "Beed", coords: [75.73, 18.99] },
    { name: "Mumbai", coords: [72.85, 19.04] },
    { name: "Latur", coords: [76.78, 18.38] },
    { name: "Osmanabad", coords: [76.02, 18.18] },
    { name: "Solapur", coords: [75.36, 17.81] },
    { name: "Satara", coords: [74.25, 17.70] },
    { name: "Ratnagiri", coords: [73.48, 17.22] },
    { name: "Sangli", coords: [74.71, 17.14] },
    { name: "Kolhapur", coords: [74.11, 16.50] },
    { name: "Sindhudurg", coords: [73.75, 16.20] },
    { name: "Thane", coords: [73.32, 19.38] },
    { name: "Palghar", coords: [72.98, 19.80] },
    { name: "Nandurbar", coords: [74.12, 21.44] },
    { name: "Amravati", coords: [77.71, 21.15] },
    { name: "Dhule", coords: [74.56, 21.07] },
    { name: "Nagpur", coords: [79.04, 21.18] },
    { name: "Aurangabad", coords: [75.36, 20.13] },
    { name: "Raigad", coords: [73.29, 18.45] },
];

const highlightedDistricts = [
    "Sangli", "Satara", "Kolhapur", "Pune", "Sindhudurg",
    "Raigad", "Ahmednagar", "Osmanabad", "Yavatmal",
    "Solapur", "Ratnagiri", "Latur", "Palghar"
];

const WorkDoneAreas = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });
    const [showDetail, setShowDetail] = useState(false);

    const [tooltipContent, setTooltipContent] = useState({});
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (isInView && !showDetail) {
            const t = setTimeout(() => setShowDetail(true), 2200);
            return () => clearTimeout(t);
        }
    }, [isInView, showDetail]);

    const handleMouseEnter = (district, isHighlighted) => {
        if (isHighlighted) setTooltipContent({ name: district, show: true });
    };
    const handleMouseMove = (e) => setTooltipPosition({ x: e.clientX + 12, y: e.clientY + 12 });
    const handleMouseLeave = () => setTooltipContent({});

    return (
        <div ref={ref}>
            <Row className="g-0 align-items-stretch">
                {/* Column 1: Text */}
                <Col lg={2} md={3} className="d-flex">
                    <div style={{
                        background: 'linear-gradient(135deg, #1B5E20 0%, #388E3C 50%, #4CAF50 100%)',
                        borderRadius: '12px 0 0 12px',
                        padding: '28px 20px',
                        color: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: '100%',
                    }}>
                        <h4 style={{ fontWeight: '800', marginBottom: '16px', lineHeight: '1.3' }}>
                            Where We Work
                        </h4>
                        <p style={{ fontSize: '0.88rem', lineHeight: '1.7', margin: 0, opacity: 0.92 }}>
                            PSGS operates across 13 districts of Maharashtra, reaching more than 3,869 villages
                            through integrated rural development initiatives.
                        </p>
                    </div>
                </Col>

                {/* Column 2: Map */}
                <Col lg={8} md={6} className="d-flex">
                    <div style={{
                        backgroundColor: '#f8faf8',
                        position: 'relative',
                        width: '100%',
                        minHeight: '520px',
                        overflow: 'hidden',
                    }}>
                        <AnimatePresence mode="wait">
                            {!showDetail ? (
                                <motion.div
                                    key="india"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0, transition: { duration: 0.4 } }}
                                    transition={{ duration: 0.7, ease: "easeOut" }}
                                    style={{
                                        position: 'absolute', inset: 0,
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', justifyContent: 'center',
                                        padding: '8px',
                                    }}
                                >
                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <ComposableMap
                                            projection="geoMercator"
                                            width={700}
                                            height={480}
                                            projectionConfig={{ scale: 850, center: [82, 23] }}
                                            style={{ width: '100%', maxHeight: '100%', display: 'block' }}
                                        >
                                            <Geographies geography={indiaGeo}>
                                                {({ geographies }) =>
                                                    geographies.map((geo) => {
                                                        const st = geo.properties.st_nm;
                                                        const isMH = st === "Maharashtra";
                                                        const isGoa = st === "Goa";
                                                        return (
                                                            <Geography
                                                                key={geo.rsmKey}
                                                                geography={geo}
                                                                stroke="#fff"
                                                                strokeWidth={0.5}
                                                                style={{
                                                                    default: { fill: isMH ? "#2E7D32" : isGoa ? "#43A047" : "#d4e6d4", outline: "none" },
                                                                    hover: { fill: isMH ? "#1B5E20" : isGoa ? "#2E7D32" : "#c5dcc5", outline: "none" },
                                                                    pressed: { outline: "none" },
                                                                }}
                                                            />
                                                        );
                                                    })
                                                }
                                            </Geographies>
                                        </ComposableMap>
                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', marginTop: '2px' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', color: '#333' }}>
                                                <span style={{ width: 10, height: 10, backgroundColor: '#2E7D32', borderRadius: 2, display: 'inline-block' }} />
                                                Maharashtra
                                            </span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', color: '#333' }}>
                                                <span style={{ width: 10, height: 10, backgroundColor: '#43A047', borderRadius: 2, display: 'inline-block' }} />
                                                Goa
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="maharashtra"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                    style={{ position: 'absolute', inset: 0 }}
                                >
                                    <ComposableMap
                                        projection="geoMercator"
                                        width={960}
                                        height={520}
                                        projectionConfig={{ scale: 3600, center: [76.5, 18.8] }}
                                        style={{ width: '100%', height: '100%', display: 'block' }}
                                    >
                                        <Geographies geography={indiaGeo}>
                                            {({ geographies }) =>
                                                geographies
                                                    .filter((geo) => geo.properties.st_nm === "Goa")
                                                    .map((geo) => (
                                                        <Geography
                                                            key={geo.rsmKey}
                                                            geography={geo}
                                                            stroke="#999"
                                                            strokeWidth={0.4}
                                                            style={{
                                                                default: { fill: "#C8E6C9", outline: "none" },
                                                                hover: { fill: "#A5D6A7", outline: "none" },
                                                                pressed: { outline: "none" },
                                                            }}
                                                        />
                                                    ))
                                            }
                                        </Geographies>
                                        <Marker coordinates={[74.0, 15.35]}>
                                            <text textAnchor="middle" style={{ fontSize: '7px', fontFamily: 'sans-serif', fontWeight: '700', fill: '#2E7D32', pointerEvents: 'none' }}>
                                                Goa
                                            </text>
                                        </Marker>
                                        <Geographies geography={maharashtraGeo}>
                                            {({ geographies }) =>
                                                geographies.map((geo) => {
                                                    const isHighlighted = highlightedDistricts.includes(geo.properties.district);
                                                    const isSangli = geo.properties.district === "Sangli";
                                                    return (
                                                        <Geography
                                                            key={geo.rsmKey}
                                                            geography={geo}
                                                            stroke="#999"
                                                            strokeWidth={0.4}
                                                            style={{
                                                                default: { fill: isSangli ? "#F8BBD0" : isHighlighted ? "#C8E6C9" : "#f0f0f0", outline: "none" },
                                                                hover: { fill: isSangli ? "#F48FB1" : isHighlighted ? "#A5D6A7" : "#e8e8e8", outline: "none" },
                                                                pressed: { fill: isSangli ? "#EC407A" : isHighlighted ? "#81C784" : "#ddd", outline: "none" },
                                                            }}
                                                            onMouseEnter={() => handleMouseEnter(geo.properties.district, isHighlighted)}
                                                            onMouseMove={handleMouseMove}
                                                            onMouseLeave={handleMouseLeave}
                                                        />
                                                    );
                                                })
                                            }
                                        </Geographies>
                                        {markerCoords.map((marker) => (
                                            <Marker key={`dot-${marker.name}`} coordinates={marker.coords}>
                                                <circle
                                                    r={marker.name === "Sangli" ? 5 : 3.5}
                                                    fill={marker.name === "Sangli" ? "#E91E63" : "#1B5E20"}
                                                    stroke="#fff"
                                                    strokeWidth={0.8}
                                                />
                                            </Marker>
                                        ))}
                                        {districtCentroids.map((d) => (
                                            <Marker key={`label-${d.name}`} coordinates={d.coords}>
                                                <text
                                                    textAnchor="middle"
                                                    dy={-5}
                                                    style={{
                                                        fontSize: '6px',
                                                        fontFamily: 'sans-serif',
                                                        fontWeight: highlightedDistricts.includes(d.name) || d.name === "Sangli" ? '700' : '400',
                                                        fill: highlightedDistricts.includes(d.name) || d.name === "Sangli" ? '#1B5E20' : '#555',
                                                        pointerEvents: 'none',
                                                    }}
                                                >
                                                    {d.name}
                                                </text>
                                            </Marker>
                                        ))}
                                    </ComposableMap>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </Col>

                {/* Column 3: Project Intervention Area */}
                <Col lg={2} md={3} className="d-flex">
                    <div style={{
                        background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 50%, #1B5E20 100%)',
                        borderRadius: '0 12px 12px 0',
                        padding: '24px 18px',
                        color: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: '100%',
                    }}>
                        <h6 style={{ fontWeight: '700', marginBottom: '12px', fontSize: '0.88rem' }}>
                            Project Intervention Area
                        </h6>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {interventionAreas.map((area, i) => (
                                <li key={i} style={{
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    padding: '3px 0',
                                    borderBottom: i < interventionAreas.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none',
                                }}>
                                    {area}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Col>
            </Row>

            {/* Mobile-specific border radius fix */}
            <style>{`
                @media (max-width: 767px) {
                    .g-0 > [class*="col-"]:first-child > div {
                        border-radius: 12px 12px 0 0 !important;
                    }
                    .g-0 > [class*="col-"]:last-child > div {
                        border-radius: 0 0 12px 12px !important;
                    }
                }
            `}</style>

            {tooltipContent.show && (
                <div style={{
                    position: "fixed",
                    top: tooltipPosition.y,
                    left: tooltipPosition.x,
                    backgroundColor: "#2E7D32",
                    color: "white",
                    padding: "6px 10px",
                    borderRadius: "5px",
                    pointerEvents: "none",
                    fontSize: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    zIndex: 1000
                }}>
                    <strong>{tooltipContent.name}</strong>
                </div>
            )}
        </div>
    );
};

export default WorkDoneAreas;
